import React, { useState } from 'react';
import { Plus, Minus, HelpCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';

const faqData = {
  de: {
    title: 'Häufig gestellte Fragen',
    subtitle: 'Alles Wichtige zu Buchung, Preisen und Ablauf Ihres Flughafentransfers.',
    items: [
      { q: 'Wie buche ich einen Flughafentransfer?', a: 'Geben Sie Abhol- und Zielort, Datum und Uhrzeit ein, wählen Sie ein Fahrzeug und Ihre Daten – fertig. Sie erhalten sofort eine Bestätigung per E-Mail.' },
      { q: 'Sind die Preise wirklich fix?', a: 'Ja. Der angezeigte Preis ist ein Festpreis inklusive Wartezeit, Steuern und Gepäck – Tag und Nacht, ohne versteckte Gebühren.' },
      { q: 'Was passiert, wenn mein Flug Verspätung hat?', a: 'Wir verfolgen Ihre Flugnummer und passen die Abholzeit automatisch an. Die Wartezeit am Flughafen ist inklusive.' },
      { q: 'Kann ich kostenlos stornieren?', a: 'Ja, bis 24 Stunden vor der Fahrt stornieren Sie kostenlos und erhalten einen Gutschein oder eine Rückerstattung.' },
      { q: 'Bieten Sie Kindersitze an?', a: 'Ja. Geben Sie bei der Buchung die Anzahl und das Alter der Kinder an, wir stellen den passenden Sitz bereit.' },
      { q: 'Wie kann ich bezahlen?', a: 'Sie zahlen bequem per Kreditkarte online oder bar direkt beim Fahrer.' },
    ],
  },
  en: {
    title: 'Frequently Asked Questions',
    subtitle: 'Everything you need to know about booking, prices and your airport transfer.',
    items: [
      { q: 'How do I book an airport transfer?', a: 'Enter your pickup and destination, date and time, choose a vehicle and add your details – done. You get an instant confirmation by email.' },
      { q: 'Are the prices really fixed?', a: 'Yes. The displayed price is a fixed price including waiting time, taxes and luggage – day and night, with no hidden fees.' },
      { q: 'What happens if my flight is delayed?', a: 'We track your flight number and adjust the pickup time automatically. Waiting time at the airport is included.' },
      { q: 'Can I cancel for free?', a: 'Yes, cancel free of charge up to 24 hours before the ride and receive a voucher or a refund.' },
      { q: 'Do you offer child seats?', a: 'Yes. Provide the number and age of children at booking and we will prepare the right seat.' },
      { q: 'How can I pay?', a: 'Pay conveniently by credit card online or in cash directly with the driver.' },
    ],
  },
  fr: {
    title: 'Questions fréquentes',
    subtitle: 'Tout ce qu’il faut savoir sur la réservation, les prix et votre transfert.',
    items: [
      { q: 'Comment réserver un transfert aéroport ?', a: 'Indiquez le lieu de prise en charge et la destination, la date et l’heure, choisissez un véhicule et vos coordonnées. Vous recevez une confirmation immédiate par e-mail.' },
      { q: 'Les prix sont-ils vraiment fixes ?', a: 'Oui. Le prix affiché est un prix fixe incluant l’attente, les taxes et les bagages – jour et nuit, sans frais cachés.' },
      { q: 'Que se passe-t-il si mon vol est retardé ?', a: 'Nous suivons votre numéro de vol et ajustons l’heure de prise en charge. L’attente à l’aéroport est incluse.' },
      { q: 'Puis-je annuler gratuitement ?', a: 'Oui, annulez gratuitement jusqu’à 24 heures avant et recevez un bon ou un remboursement.' },
      { q: 'Proposez-vous des sièges enfant ?', a: 'Oui. Indiquez le nombre et l’âge des enfants à la réservation et nous préparons le siège adapté.' },
      { q: 'Comment puis-je payer ?', a: 'Payez facilement par carte en ligne ou en espèces directement au chauffeur.' },
    ],
  },
  it: {
    title: 'Domande frequenti',
    subtitle: 'Tutto ciò che devi sapere su prenotazione, prezzi e transfer.',
    items: [
      { q: 'Come prenoto un transfer aeroportuale?', a: 'Inserisci ritiro e destinazione, data e ora, scegli un veicolo e i tuoi dati. Ricevi subito una conferma via e-mail.' },
      { q: 'I prezzi sono davvero fissi?', a: 'Sì. Il prezzo mostrato è fisso e include attesa, tasse e bagagli – giorno e notte, senza costi nascosti.' },
      { q: 'Cosa succede se il volo è in ritardo?', a: 'Monitoriamo il numero del volo e adeguiamo l’orario di ritiro. L’attesa in aeroporto è inclusa.' },
      { q: 'Posso cancellare gratuitamente?', a: 'Sì, cancella gratis fino a 24 ore prima e ricevi un buono o un rimborso.' },
      { q: 'Offrite seggiolini per bambini?', a: 'Sì. Indica numero ed età dei bambini alla prenotazione e prepareremo il seggiolino adatto.' },
      { q: 'Come posso pagare?', a: 'Paga comodamente con carta online o in contanti direttamente all’autista.' },
    ],
  },
};

const FAQ = ({ heading: H = 'h2' }) => {
  const { lang } = useApp();
  const data = faqData[lang] || faqData.en;
  const [open, setOpen] = useState(0);
  return (
    <section id="help" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ backgroundColor: 'var(--brand-muted)' }}>
            <HelpCircle className="w-7 h-7" style={{ color: 'var(--brand-blue)' }} />
          </div>
          <H className="text-3xl sm:text-4xl font-extrabold text-[color:var(--brand-dark)]">{data.title}</H>
          <p className="mt-4 text-gray-500 leading-relaxed">{data.subtitle}</p>
        </div>
        <div className="space-y-3">
          {data.items.map((it, i) => (
            <div key={i} className="border border-gray-100 rounded-xl overflow-hidden bg-white shadow-sm">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-bold text-[color:var(--brand-dark)] pr-4">{it.q}</span>
                <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: open === i ? 'var(--brand-dark)' : 'var(--brand-muted)' }}>
                  {open === i ? <Minus className="w-4 h-4" style={{ color: 'var(--brand-accent)' }} /> : <Plus className="w-4 h-4 text-[color:var(--brand-dark)]" />}
                </span>
              </button>
              <div className={`px-5 overflow-hidden transition-all duration-300 ${open === i ? 'max-h-48 pb-5' : 'max-h-0'}`}>
                <p className="text-sm text-gray-500 leading-relaxed">{it.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
